import { Suspense, lazy, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import ContextChips, { CONTEXT_LINE } from './ContextChips';
import Faq from './Faq';
import { DEFINITION, HOME_FAQ, absoluteUrl, faqSchema, softwareApplicationSchema } from './seo';
import { TESTIMONIALS } from './testimonials';
import { useLocation } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import HomeFeatures from './HomeFeatures';
import StoreButtons from './StoreButtons';
import './MobileDownloadGate.css';

const MockupPage = lazy(() => import('./mockups/MockupPage'));

const MOBILE_MEDIA_QUERY = '(max-width: 767px)';
const WEB_APP_URL = 'https://app.horsestat.com';

/*
 * Hero recording — asset spec (feature deck clips: see HomeFeatures.js)
 * ---------------------------------------------------------------------------
 * Record the app screen raw (no device frame; the bezel is drawn in CSS).
 *   - Portrait 9:19.5, export at 720x1560 (or 886x1920), 24–30 fps.
 *   - Encode both H.264 MP4 and VP9 WebM, no audio track (videos are muted).
 *   - Hero tour: 20–30 s, ≤ 4 MB.
 *   - Light mode throughout, consistent status bar (e.g. 9:41, full signal).
 *   - Export the first frame as a JPEG to use as the `poster`.
 *   - Drop files into public/videos/app/ using the paths below.
 *
 * Caption timing: each cue's `at` is the second in the hero recording where
 * that highlight starts. Tune these once the recording exists. Until a video
 * file is present the captions rotate on a timer over the poster image.
 */
const MOBILE_DESCRIPTION =
  'HorseStat puts every UK & Irish runner\u2019s form, pace, going, class, trainer, jockey, sire and dam stats in one interactive racecard, ranked by a predictive rating from 10,000 race simulations. Free to start on iPhone, Android and web.';

const HERO_TOUR = {
  mp4: '/videos/app/hero-tour.mp4',
  webm: '/videos/app/hero-tour.webm',
  poster: '/videos/app/hero-tour.jpg',
  fallbackDuration: 24,
  cues: [
    { at: 0, text: 'Interactive racecards for UK & Irish racing' },
    { at: 3.5, text: 'Complete form history for every runner' },
    { at: 7, text: '22 analysis tables, one clean view' },
    { at: 10.5, text: 'Horse, jockey, trainer, sire & dam insights' },
    { at: 14, text: 'Runner highlights spot trends instantly' },
    { at: 17.5, text: 'Predictive ratings from thousands of simulations' },
    { at: 21, text: 'Everything you need to make better decisions' },
  ],
};

function tryPlay(videoEl) {
  if (!videoEl) return;
  try {
    const result = videoEl.play();
    if (result && typeof result.catch === 'function') result.catch(() => {});
  } catch (error) {
    // Autoplay can be refused; the poster stays visible.
  }
}

/* CSS-drawn iPhone bezel. `size` selects the width preset. */
function PhoneFrame({ size = 'hero', className = '', children }) {
  return (
    <div className={`phone-frame phone-frame--${size} ${className}`.trim()}>
      <div className="phone-frame__button phone-frame__button--action" aria-hidden="true" />
      <div className="phone-frame__button phone-frame__button--volume-up" aria-hidden="true" />
      <div className="phone-frame__button phone-frame__button--volume-down" aria-hidden="true" />
      <div className="phone-frame__button phone-frame__button--power" aria-hidden="true" />
      <div className="phone-frame__screen">
        {children}
        <div className="phone-frame__island" aria-hidden="true" />
      </div>
    </div>
  );
}

function cueIndexForTime(cues, time) {
  let index = 0;
  for (let i = 0; i < cues.length; i += 1) {
    if (cues[i].at <= time) index = i;
  }
  return index;
}

function HeroTour({ tour }) {
  const { cues } = tour;
  const videoRef = useRef(null);
  const stageRef = useRef(null);
  const underlineRef = useRef(null);
  const trackRef = useRef(null);
  const textRef = useRef(null);
  const [index, setIndex] = useState(0);
  const [videoActive, setVideoActive] = useState(false); // true once the video is actually playing
  const [videoFailed, setVideoFailed] = useState(false);

  const timerMode = !videoActive;
  const timerStepMs = (tour.fallbackDuration / cues.length) * 1000;

  // Timer mode: rotate captions over the poster until the video plays. The
  // underline animates over the same duration in CSS, so when it reaches the
  // right edge the next caption takes over.
  useEffect(() => {
    if (!timerMode) return undefined;
    const timeout = window.setTimeout(() => {
      setIndex((current) => (current + 1) % cues.length);
    }, timerStepMs);
    return () => window.clearTimeout(timeout);
  }, [timerMode, index, cues.length, timerStepMs]);

  // Video mode: drive the underline from the playhead every frame so it moves
  // smoothly rather than in ~250ms `timeupdate` steps.
  useEffect(() => {
    if (timerMode || typeof requestAnimationFrame === 'undefined') return undefined;
    let frame = 0;
    const tick = () => {
      const video = videoRef.current;
      const underline = underlineRef.current;
      if (video && underline) {
        const time = video.currentTime || 0;
        const cueIndex = cueIndexForTime(cues, time);
        const start = cues[cueIndex].at;
        const end = cueIndex + 1 < cues.length ? cues[cueIndex + 1].at : video.duration || tour.fallbackDuration;
        const fill = end > start ? Math.min(1, Math.max(0, (time - start) / (end - start))) : 0;
        underline.style.transform = `scaleX(${fill})`;
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [timerMode, cues, tour.fallbackDuration]);

  // Size the underline to the rendered text (widest wrapped line), not the box.
  useLayoutEffect(() => {
    const fit = () => {
      const text = textRef.current;
      const track = trackRef.current;
      if (!text || !track || typeof document.createRange !== 'function') return;
      const range = document.createRange();
      range.selectNodeContents(text);
      if (typeof range.getClientRects !== 'function') return; // jsdom
      const rects = Array.from(range.getClientRects()).filter((rect) => rect.width > 0);
      if (!rects.length) return;
      const left = Math.min(...rects.map((rect) => rect.left));
      const right = Math.max(...rects.map((rect) => rect.right));
      const parent = track.parentElement.getBoundingClientRect();
      track.style.left = `${left - parent.left}px`;
      track.style.width = `${right - left}px`;
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [index]);

  // Pause when the hero scrolls out of view, resume when it returns.
  useEffect(() => {
    const stage = stageRef.current;
    const video = videoRef.current;
    if (!stage || !video || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) tryPlay(video);
        else video.pause();
      },
      { threshold: 0.2 }
    );
    observer.observe(stage);
    return () => observer.disconnect();
  }, []);

  // Autoplay retry on first interaction (iOS Safari occasionally needs it).
  useEffect(() => {
    const retry = () => tryPlay(videoRef.current);
    window.addEventListener('touchstart', retry, { passive: true, once: true });
    return () => window.removeEventListener('touchstart', retry);
  }, []);

  const handleTimeUpdate = (event) => {
    const video = event.currentTarget;
    setVideoActive(true);
    setIndex(cueIndexForTime(cues, video.currentTime || 0));
  };

  const active = cues[index];

  return (
    <div className="hero-tour">
      <div ref={stageRef} className="hero-tour__stage">
        <PhoneFrame size="hero">
          {videoFailed ? (
            <img src={tour.poster} alt="HorseStat app walkthrough" className="app-clip app-clip--poster" />
          ) : (
            <video
              ref={videoRef}
              className="app-clip"
              data-testid="hero-tour-video"
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              poster={tour.poster}
              aria-hidden="true"
              onTimeUpdate={handleTimeUpdate}
              onPause={() => setVideoActive(false)}
            >
              <source src={tour.webm} type="video/webm" />
              <source src={tour.mp4} type="video/mp4" onError={() => setVideoFailed(true)} />
            </video>
          )}
        </PhoneFrame>
      </div>

      <div
        className={`hero-tour__caption${timerMode ? ' hero-tour__caption--timer' : ''}`}
        style={timerMode ? { '--cue-ms': `${timerStepMs}ms` } : undefined}
        aria-live="polite"
      >
        <span key={index} className="hero-tour__caption-inner">
          <span ref={textRef} className="hero-tour__caption-text">{active.text}</span>
          <span
            ref={trackRef}
            className="hero-tour__underline"
            role="progressbar"
            aria-label={`Highlight ${index + 1} of ${cues.length}`}
            aria-valuemin={1}
            aria-valuemax={cues.length}
            aria-valuenow={index + 1}
          >
            <span ref={underlineRef} className="hero-tour__underline-fill" />
          </span>
        </span>
      </div>
    </div>
  );
}

function HorseMark({ className = '' }) {
  return <img src="/horsestat-transparent.png" alt="HorseStat logo" width="64" height="64" className={className} />;
}

function getIsMobileViewport() {
  return typeof window !== 'undefined' && window.matchMedia(MOBILE_MEDIA_QUERY).matches;
}

/* The mobile landing page. `features` swaps the feature block (used by /mockups). */
export function MobileHome({ features = <HomeFeatures /> }) {
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setActiveTestimonial((current) => (current + 1) % TESTIMONIALS.length);
    }, 7000);

    return () => window.clearInterval(interval);
  }, []);

  return (
    <div className="mobile-download">
      <Helmet>
        <title>HorseStat - Data-Driven Horse Racing Analysis</title>
        <meta name="description" content={MOBILE_DESCRIPTION} />
        <link rel="canonical" href={absoluteUrl('/')} />
        <meta property="og:title" content="HorseStat - Data-Driven Horse Racing Analysis" />
        <meta property="og:description" content={DEFINITION} />
        <meta property="og:url" content={absoluteUrl('/')} />
        <meta name="twitter:title" content="HorseStat - Data-Driven Horse Racing Analysis" />
        <meta name="twitter:description" content={DEFINITION} />
        <script type="application/ld+json">{JSON.stringify(softwareApplicationSchema())}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema(HOME_FAQ))}</script>
      </Helmet>
      <Navbar />
      <main aria-labelledby="mobile-download-title">
        <section className="mobile-hero">
          <div className="mobile-hero__content">
            <p className="mobile-eyebrow">Race day, simplified</p>
            <h1 id="mobile-download-title">
              Every angle.
              <br />
              Right in your pocket.
            </h1>
            <StoreButtons />
          </div>
          <HeroTour tour={HERO_TOUR} />
        </section>

        <section className="mobile-points" aria-label="HorseStat highlights">
          <div className="mobile-points__item">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M4 19V5m0 14h16M8 15l3-3 3 2 5-6" />
            </svg>
            <span>22 data points per horse</span>
          </div>
          <div className="mobile-points__item">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="12" cy="12" r="9" />
              <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
            </svg>
            <span>UK &amp; Irish racing</span>
          </div>
          <div className="mobile-points__item">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 3v9l6 3" />
              <circle cx="12" cy="12" r="9" />
            </svg>
            <span>Fresh insights every day</span>
          </div>
        </section>

        <section className="mobile-section mobile-context">
          <p className="mobile-eyebrow">Better decisions start here</p>
          <h2>Profitable betting starts with better context.</h2>
          <p className="mobile-section__copy">{CONTEXT_LINE}</p>
          <ContextChips className="context-chips--light" />
        </section>

        {features}

        <section className="mobile-section mobile-proof">
          <p className="mobile-eyebrow">Trusted by racing fans</p>
          {/* Every quote is in the DOM (crawlable); slides share one grid cell so the height never jumps. */}
          <div className="mobile-proof__slides">
            {TESTIMONIALS.map((item, quoteIndex) => (
              <figure
                key={item.id}
                className={`mobile-proof__slide${quoteIndex === activeTestimonial ? ' is-active' : ''}`}
                aria-hidden={quoteIndex !== activeTestimonial}
              >
                <p className="mobile-proof__pull">“{item.pull}”</p>
                <blockquote className="mobile-proof__quote">{item.short}</blockquote>
              </figure>
            ))}
          </div>
          <div className="mobile-proof__footer">
            <div className="mobile-proof__person">
              <span className="mobile-proof__avatar" aria-hidden="true">
                {TESTIMONIALS[activeTestimonial].initials}
              </span>
              <p className="mobile-proof__author">{TESTIMONIALS[activeTestimonial].author}</p>
            </div>
            <div className="mobile-proof__dots" role="group" aria-label="Choose testimonial">
              {TESTIMONIALS.map((item, dotIndex) => (
                <button
                  key={item.id}
                  type="button"
                  className={dotIndex === activeTestimonial ? 'is-active' : ''}
                  onClick={() => setActiveTestimonial(dotIndex)}
                  aria-label={`Show testimonial ${dotIndex + 1}`}
                  aria-pressed={dotIndex === activeTestimonial}
                >
                  <span />
                </button>
              ))}
            </div>
          </div>
        </section>

        <Faq />

        <section className="mobile-section mobile-final-cta">
          <HorseMark className="mobile-final-cta__mark" />
          <h2>Your next race starts here.</h2>
          <p className="mobile-section__copy">
            Download HorseStat and see the complete picture before the market moves.
          </p>
          <StoreButtons className="store-buttons--stacked" />
          <p className="mobile-final-cta__desktop">
            Also available on desktop at{' '}
            <a href={WEB_APP_URL} target="_blank" rel="noopener noreferrer">
              app.horsestat.com
            </a>{' '}
            — your account syncs across devices.
          </p>
        </section>
      </main>
      <Footer />
    </div>
  );
}

const MOCKUP_PATH = /^\/mockups\/([a-z])\/?$/;

function MobileDownloadGate({ children }) {
  const { pathname } = useLocation();
  const [isMobileViewport, setIsMobileViewport] = useState(getIsMobileViewport);

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
    const updateViewport = (event) => setIsMobileViewport(event.matches);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', updateViewport);
      return () => mediaQuery.removeEventListener('change', updateViewport);
    }

    mediaQuery.addListener(updateViewport);
    return () => mediaQuery.removeListener(updateViewport);
  }, []);

  const mockup = pathname.match(MOCKUP_PATH);
  if (mockup) {
    return (
      <Suspense fallback={null}>
        <MockupPage variant={mockup[1]} isMobileViewport={isMobileViewport} />
      </Suspense>
    );
  }

  if (!isMobileViewport || pathname !== '/') {
    return children;
  }

  return <MobileHome />;
}

export default MobileDownloadGate;
